// components/company/tabs/CompanyRoles.jsx
import { useState, Fragment, useCallback, useRef, useEffect, useMemo } from "react";
import { Field } from "../../ui/Field";
import { addCompanyRole, updateCompanyRole, deleteCompanyRole, searchExistingUbos } from "../../../api";
import { titleCase } from "../../../utils/helpers";

const ROLE_OPTIONS = [
  { value: "ubo", label: "UBO" },
  { value: "director", label: "Director" },
  { value: "shareholder", label: "Shareholder" },
  { value: "board_member", label: "Board Member" },
  { value: "supervisory_board", label: "Supervisory Board" },
  { value: "authorized_signatory", label: "Authorized Signatory" },
];

const ROLES_WITH_SHARES = ["ubo", "shareholder", "director", "board_member"];

const SEARCH_DEBOUNCE = 350;
const MIN_FIRST_OR_LAST = 2;
const MIN_COMBINED = 3;

function PersonSuggestionModal({ isOpen, suggestions, onClose, onUseExisting, onCreateNew }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <h4>Similar person found</h4>
        <p>We found similar people in our database:</p>
        {suggestions.map((person) => (
          <div
            key={person.id}
            className="card"
            style={{
              padding: 12,
              marginBottom: 8,
              border: "1px solid #dadce0",
              cursor: "pointer",
            }}
            onClick={() => onUseExisting(person)}>
            <div style={{ fontWeight: 500 }}>
              {person.first_name} {person.last_name}
            </div>
            <div style={{ fontSize: 12, color: "#5f6368", marginTop: 4 }}>
              {person.country_name || person.country_iso2 || "—"} • ID: {person.id}
            </div>
            {person.companies && <div style={{ fontSize: 12, color: "#5f6368", marginTop: 4 }}>Companies: {person.companies}</div>}
            <button
              type="button"
              style={{ marginTop: 8, fontSize: 13 }}
              onClick={(e) => {
                e.stopPropagation();
                onUseExisting(person);
              }}>
              Use this person
            </button>
          </div>
        ))}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button type="button" onClick={onCreateNew}>
            Create new person anyway
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareBar({ percent }) {
  const pct = Math.min(100, Math.max(0, percent || 0));
  const color = pct > 0 ? "#1a73e8" : "#dadce0";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 8,
          backgroundColor: "#f1f3f4",
          borderRadius: 4,
          overflow: "hidden",
          minWidth: 100,
        }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            backgroundColor: color,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, minWidth: 45, textAlign: "right", color: "#5f6368" }}>{pct.toFixed(1)}%</div>
    </div>
  );
}

function OwnershipSummary({ roles }) {
  const rolesWithShares = roles.filter((r) => ROLES_WITH_SHARES.includes(r.role) && r.share_pct !== null && r.share_pct !== undefined);

  if (rolesWithShares.length === 0) return null;

  // Calculate totals by category
  const ubos = roles.filter((r) => r.role === "ubo");
  const uboTotal = ubos.reduce((sum, r) => sum + (parseFloat(r.share_pct) || 0), 0);

  const otherShareholders = roles.filter((r) => r.role !== "ubo" && ROLES_WITH_SHARES.includes(r.role) && r.share_pct !== null && r.share_pct !== undefined);
  const otherTotal = otherShareholders.reduce((sum, r) => sum + (parseFloat(r.share_pct) || 0), 0);

  const allocatedTotal = uboTotal + otherTotal;
  const unallocated = Math.max(0, 100 - allocatedTotal);

  // Status logic
  let statusColor = "#5f6368";
  let statusText = "Tracked";
  let statusIcon = "●";

  if (allocatedTotal > 100) {
    statusColor = "#ea4335";
    statusText = "Over-allocated";
    statusIcon = "⚠";
  } else if (allocatedTotal === 100) {
    statusColor = "#34a853";
    statusText = "Fully allocated";
    statusIcon = "✓";
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16, backgroundColor: "#fff", border: "1px solid #dadce0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: "#202124" }}>Ownership Structure</h4>
          <div style={{ fontSize: 13, color: "#5f6368", marginTop: 4 }}>Share distribution across all stakeholders</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: statusColor, fontWeight: 500 }}>
          <span>{statusIcon}</span>
          <span>{statusText}</span>
        </div>
      </div>

      {/* Main allocation bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1, height: 12, backgroundColor: "#f1f3f4", borderRadius: 6, overflow: "hidden", display: "flex" }}>
            {uboTotal > 0 && (
              <div
                style={{
                  width: `${Math.min(100, uboTotal)}%`,
                  height: "100%",
                  backgroundColor: "#1a73e8",
                  transition: "width 0.3s ease",
                }}
                title={`UBOs: ${uboTotal.toFixed(1)}%`}
              />
            )}
            {otherTotal > 0 && (
              <div
                style={{
                  width: `${Math.min(100 - uboTotal, otherTotal)}%`,
                  height: "100%",
                  backgroundColor: "#34a853",
                  transition: "width 0.3s ease",
                }}
                title={`Other shareholders: ${otherTotal.toFixed(1)}%`}
              />
            )}
          </div>
          <div style={{ fontSize: 18, fontWeight: 500, minWidth: 70, textAlign: "right", color: "#202124" }}>{allocatedTotal.toFixed(1)}%</div>
        </div>
        <div style={{ fontSize: 12, color: "#5f6368", textAlign: "right" }}>of 100% allocated</div>
      </div>

      {/* Breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, paddingTop: 16, borderTop: "1px solid #e8eaed" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#5f6368", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Ultimate Beneficial Owners</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 500, color: "#1a73e8" }}>{uboTotal.toFixed(1)}%</div>
            <div style={{ fontSize: 13, color: "#5f6368" }}>
              ({ubos.length} {ubos.length === 1 ? "person" : "people"})
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#5f6368", marginTop: 4 }}>≥25% ownership each</div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: "#5f6368", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Other Shareholders</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 500, color: "#34a853" }}>{otherTotal.toFixed(1)}%</div>
            <div style={{ fontSize: 13, color: "#5f6368" }}>
              ({otherShareholders.length} {otherShareholders.length === 1 ? "person" : "people"})
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#5f6368", marginTop: 4 }}>Directors, shareholders, board members</div>
        </div>
      </div>

      {unallocated > 0 && (
        <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f8f9fa", borderRadius: 4 }}>
          <div style={{ fontSize: 13, color: "#5f6368" }}>
            <span style={{ fontWeight: 500 }}>{unallocated.toFixed(1)}%</span> unallocated or held by other entities
          </div>
        </div>
      )}
    </div>
  );
}

export const CompanyRoles = ({ roles, countries, companyId, notify, refetch }) => {
  const [editRole, setEditRole] = useState(null);
  const [newRole, setNewRole] = useState({
    role: "ubo",
    first_name: "",
    last_name: "",
    country_iso2: "",
    start_date: "",
    share_pct: "",
    _is_existing: false,
    _existing_id: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const searchTimerRef = useRef(null);
  const searchCacheRef = useRef(new Map());
  const searchSeqRef = useRef(0);
  const latestHandledRef = useRef(0);

  const roleDisplayName = (role) =>
    role.person_name ||
    (role.first_name || role.last_name ? [role.first_name, role.last_name].filter(Boolean).join(" ") : "") ||
    (role.person && (role.person.full_name || [role.person.first_name, role.person.last_name].filter(Boolean).join(" "))) ||
    "—";

  const getRoleLabel = (roleValue) => {
    const option = ROLE_OPTIONS.find((r) => r.value === roleValue);
    return option ? option.label : titleCase(roleValue);
  };

  const searchExistingPersons = useCallback(async (firstName, lastName, country) => {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    const c = (country || "").trim();
    const combined = `${f}${l}`;

    if (!c || (f.length < MIN_FIRST_OR_LAST && l.length < MIN_FIRST_OR_LAST) || combined.length < MIN_COMBINED) {
      return [];
    }

    const key = `${c}|${f.toLowerCase()}|${l.toLowerCase()}`;
    const cached = searchCacheRef.current.get(key);
    if (cached) return cached;

    const seq = ++searchSeqRef.current;

    try {
      const persons = await searchExistingUbos(f, l, c);

      if (seq >= latestHandledRef.current) {
        latestHandledRef.current = seq;
        searchCacheRef.current.set(key, persons);
      }

      return persons;
    } catch (error) {
      console.warn("Person search failed:", error);
      return [];
    }
  }, []);

  const updateNewRole = useCallback(
    (patch) => {
      setNewRole((prev) => ({ ...prev, ...patch }));

      if (patch.first_name !== undefined || patch.last_name !== undefined || patch.country_iso2 !== undefined) {
        if (searchTimerRef.current) {
          clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(async () => {
          setNewRole((current) => {
            if (current._is_existing) return current;

            const f = (current.first_name || "").trim();
            const l = (current.last_name || "").trim();
            const c = (current.country_iso2 || "").trim();
            const combined = `${f}${l}`;

            if (!c || (f.length < MIN_FIRST_OR_LAST && l.length < MIN_FIRST_OR_LAST) || combined.length < MIN_COMBINED) {
              return current;
            }

            searchExistingPersons(f, l, c).then((results) => {
              if (results.length > 0) {
                setSuggestions(results);
                setShowModal(true);
              }
            });

            return current;
          });
        }, SEARCH_DEBOUNCE);
      }
    },
    [searchExistingPersons]
  );

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const handleUseExisting = (person) => {
    setNewRole((prev) => ({
      ...prev,
      first_name: person.first_name,
      last_name: person.last_name,
      country_iso2: person.country_iso2 || prev.country_iso2,
      _is_existing: true,
      _existing_id: person.id,
    }));
    setShowModal(false);
    setSuggestions([]);
  };

  const handleCreateNew = () => {
    setNewRole((prev) => ({ ...prev, _is_existing: false, _existing_id: null }));
    setShowModal(false);
    setSuggestions([]);
  };

  const validateNewRole = () => {
    const newErrors = {};
    if (!newRole.first_name?.trim()) newErrors.first_name = "First name is required";
    if (!newRole.last_name?.trim()) newErrors.last_name = "Last name is required";
    if (!newRole.role?.trim()) newErrors.role = "Role is required";
    if (ROLES_WITH_SHARES.includes(newRole.role) && newRole.share_pct) {
      const pct = parseFloat(newRole.share_pct);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        newErrors.share_pct = "Share % must be between 0 and 100";
      }
    }
    return newErrors;
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    const newErrors = validateNewRole();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        role: newRole.role,
        start_date: newRole.start_date || null,
        share_pct: ROLES_WITH_SHARES.includes(newRole.role) && newRole.share_pct ? parseFloat(newRole.share_pct) : undefined,
        person: newRole._is_existing
          ? { id: newRole._existing_id }
          : {
              first_name: newRole.first_name,
              last_name: newRole.last_name,
              country_iso2: newRole.country_iso2 || undefined,
            },
      };

      await addCompanyRole(companyId, payload);

      setNewRole({
        role: "ubo",
        first_name: "",
        last_name: "",
        country_iso2: "",
        start_date: "",
        share_pct: "",
        _is_existing: false,
        _existing_id: null,
      });
      setErrors({});
      await refetch();
      notify("Role added successfully");
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditRole = (role) => {
    setEditRole({
      id: role.id,
      role: role.role,
      start_date: role.start_date ? role.start_date.slice(0, 10) : "",
      end_date: role.end_date ? role.end_date.slice(0, 10) : "",
      share_pct: role.share_pct || "",
    });
  };

  const saveRoleEdit = async () => {
    if (!editRole) return;

    try {
      const payload = {
        role: editRole.role,
        start_date: editRole.start_date || null,
        end_date: editRole.end_date || null,
      };

      if (ROLES_WITH_SHARES.includes(editRole.role) && editRole.share_pct !== undefined) {
        payload.share_pct = editRole.share_pct ? parseFloat(editRole.share_pct) : null;
      }

      await updateCompanyRole(companyId, editRole.id, payload);
      setEditRole(null);
      await refetch();
      notify("Role updated successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const removeRole = async (roleId) => {
    if (!confirm("Delete role?")) return;

    try {
      await deleteCompanyRole(companyId, roleId);
      await refetch();
      notify("Role deleted successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const showShareColumn = useMemo(() => roles.some((r) => ROLES_WITH_SHARES.includes(r.role)), [roles]);

  return (
    <>
      <OwnershipSummary roles={roles} />

      <div className="card table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Person</th>
              {showShareColumn && <th>Share</th>}
              <th>Start</th>
              <th>End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => {
              const editing = editRole && editRole.id === role.id;
              return (
                <Fragment key={`role-${role.id}`}>
                  <tr>
                    <td>{getRoleLabel(role.role)}</td>
                    <td>{roleDisplayName(role)}</td>
                    {showShareColumn && <td>{ROLES_WITH_SHARES.includes(role.role) ? <ShareBar percent={parseFloat(role.share_pct) || 0} /> : "—"}</td>}
                    <td>{role.start_date?.slice(0, 10) || "—"}</td>
                    <td>{role.end_date?.slice(0, 10) || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!editing && (
                        <>
                          <button type="button" onClick={() => startEditRole(role)}>
                            Edit
                          </button>
                          <button type="button" className="secondary" onClick={() => removeRole(role.id)} style={{ marginLeft: 4 }}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {editing && (
                    <tr className="inline-editor-row">
                      <td colSpan={showShareColumn ? 6 : 5}>
                        <form onSubmit={(e) => e.preventDefault()}>
                          <div className="inline-editor">
                            <label>
                              <span>Role</span>
                              <select value={editRole.role} onChange={(e) => setEditRole({ ...editRole, role: e.target.value })}>
                                {ROLE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {ROLES_WITH_SHARES.includes(editRole.role) && (
                              <label>
                                <span>Share %</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={editRole.share_pct}
                                  onChange={(e) => setEditRole({ ...editRole, share_pct: e.target.value })}
                                />
                              </label>
                            )}
                            <label>
                              <span>Start</span>
                              <input type="date" value={editRole.start_date} onChange={(e) => setEditRole({ ...editRole, start_date: e.target.value })} />
                            </label>
                            <label>
                              <span>End</span>
                              <input type="date" value={editRole.end_date} onChange={(e) => setEditRole({ ...editRole, end_date: e.target.value })} />
                            </label>
                          </div>
                          <div className="inline-editor__actions">
                            <button type="button" onClick={saveRoleEdit}>
                              Save
                            </button>
                            <button type="button" className="secondary" onClick={() => setEditRole(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {roles.length === 0 && (
              <tr>
                <td colSpan={showShareColumn ? 6 : 5} className="text-center">
                  No roles yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form className="card form-section" onSubmit={handleAddRole}>
        <h3>Add role</h3>
        {newRole._is_existing && (
          <div
            style={{
              padding: 12,
              marginBottom: 16,
              backgroundColor: "#e8f0fe",
              border: "1px solid #1a73e8",
              borderRadius: 4,
              fontSize: 13,
              color: "#1967d2",
            }}>
            Using existing person (ID: {newRole._existing_id})
          </div>
        )}
        <div className="form-grid">
          <Field label="Role" required error={errors.role}>
            <select value={newRole.role} onChange={(e) => updateNewRole({ role: e.target.value })} required>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Start date">
            <input type="date" value={newRole.start_date} onChange={(e) => updateNewRole({ start_date: e.target.value })} />
          </Field>

          <Field label="First name" required error={errors.first_name}>
            <input value={newRole.first_name} onChange={(e) => updateNewRole({ first_name: e.target.value })} required />
          </Field>

          <Field label="Last name" required error={errors.last_name}>
            <input value={newRole.last_name} onChange={(e) => updateNewRole({ last_name: e.target.value })} required />
          </Field>

          <Field label="Country">
            <select value={newRole.country_iso2} onChange={(e) => updateNewRole({ country_iso2: e.target.value })}>
              <option value="">— Select country —</option>
              {countries.map((c) => (
                <option key={c.iso2} value={c.iso2}>
                  {c.name} ({c.iso2})
                </option>
              ))}
            </select>
          </Field>

          {ROLES_WITH_SHARES.includes(newRole.role) && (
            <Field label="Share %" error={errors.share_pct}>
              <input type="number" min="0" max="100" step="0.01" value={newRole.share_pct} onChange={(e) => updateNewRole({ share_pct: e.target.value })} placeholder="25.00" />
            </Field>
          )}
        </div>
        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Adding..." : "Add role"}
          </button>
        </div>
      </form>

      <PersonSuggestionModal isOpen={showModal} suggestions={suggestions} onClose={() => setShowModal(false)} onUseExisting={handleUseExisting} onCreateNew={handleCreateNew} />
    </>
  );
};
